"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import ArticleCardSkeleton from '@/components/ui/ArticleCardSkeleton';
import ArticleSearch from '@/components/ui/ArticleSearch';
import { ContentfulArticle, formatDate, formatContentfulImageUrl } from '@/lib/contentful';
import { SearchResult } from '@/lib/articleSearch';
import { extractCategoryFromTags } from '@/lib/articleCategories';
import styles from '../../app/articles/articles-page.module.css';
import searchStyles from '@/components/ui/ArticleSearch.module.css';
import Icon, { isIconName } from './Icon';

interface EnhancedArticlesPageProps {
  initialArticles: ContentfulArticle[];
}

interface EnhancedArticleCardProps {
  article: SearchResult;
  isFeatured?: boolean;
  priority?: boolean;
  showCategory?: boolean;
}

function EnhancedArticleCard({ article, isFeatured = false, priority = false, showCategory = true }: EnhancedArticleCardProps) {
  const category = article.category || extractCategoryFromTags(article.tags);
  
  const cardContent = (
    <>
      <div className={isFeatured ? styles.featuredArticleImage : styles.articleImage}>
        {article.featuredImage ? (
          <Image 
            src={formatContentfulImageUrl(article.featuredImage.fields.file.url)}
            alt={article.featuredImage.fields.title || article.title}
            fill
            style={{ objectFit: 'cover' }}
            priority={priority}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #F5F4EF, #E7E3D9)', color: '#0B1F3B' }}>
            <Icon name={isIconName(category?.icon) ? category.icon : 'document'} size={40} strokeWidth={1.25} />
          </div>
        )}
      </div>
      
      <div className={isFeatured ? styles.featuredArticleContent : styles.articleContent}>
        {/* Category badge */}
        {showCategory && category && (
          <div className={styles.categoryBadge}>
            <span className={styles.categoryIcon}>{isIconName(category.icon) && <Icon name={category.icon} size={13} strokeWidth={1.75} />}</span>
            <span className={styles.categoryName}>{category.name}</span>
          </div>
        )}
        
        {/* Tags */}
        <div className={styles.tagsContainer}>
          {article.tags.slice(0, 3).map((tag: string, index: number) => (
            <span key={index} className={styles.tag}>
              {tag}
            </span>
          ))}
          {article.tags.length > 3 && (
            <span className={styles.tag}>+{article.tags.length - 3}</span>
          )}
        </div>
        
        <h2 className={isFeatured ? styles.featuredArticleTitle : styles.articleTitle}>
          {article.title}
        </h2>
        
        <p className={`${styles.articleExcerpt} ${isFeatured ? styles.featuredArticleExcerpt : ''}`}>
          {article.excerpt}
        </p>
        
        <div className={styles.articleMeta}>
          <div className={styles.authorInfo}>
            {article.author && article.author.fields.avatar ? (
              <div className={styles.authorAvatar}>
                <Image 
                  src={formatContentfulImageUrl(article.author.fields.avatar.fields.file.url)}
                  alt={article.author.fields.name}
                  fill
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ) : null}
            <span>{article.author ? article.author.fields.name : 'Bemoi George'}</span>
          </div>
          <time dateTime={article.publishDate}>{formatDate(article.publishDate)}</time>
        </div>
        
        {isFeatured && (
          <span className={styles.readMoreLink}>
            Read Article 
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
          </span>
        )}
      </div>
    </>
  );

  return (
    <Link 
      href={`/articles/${article.slug}`}
      className={isFeatured ? styles.featuredArticle : styles.articleCard}
    >
      {cardContent}
    </Link>
  );
}

interface ArticlesGridProps {
  articles: SearchResult[];
  isLoading: boolean;
  searchActive: boolean;
}

function ArticlesGrid({ articles, isLoading, searchActive }: ArticlesGridProps) {
  if (isLoading) {
    return (
      <div className={styles.articlesGrid}>
        <div className={styles.featuredArticle}>
          <ArticleCardSkeleton featured={true} />
        </div>
        {[...Array(5)].map((_, i) => (
          <ArticleCardSkeleton key={i} />
        ))}
      </div>
    );
  }
  
  if (articles.length === 0) {
    return (
      <div className={searchStyles.noResults}>
        <div className={searchStyles.noResultsIcon}><Icon name="search" size={36} strokeWidth={1.25} /></div>
        <h3 className={searchStyles.noResultsTitle}>
          {searchActive ? 'No articles found' : 'No articles available'}
        </h3>
        <p className={searchStyles.noResultsText}>
          {searchActive 
            ? 'Try adjusting your search criteria or browse by category.'
            : 'Check back soon for insightful content about digital marketing and SEO!'
          }
        </p>
      </div>
    );
  }
  
  // For search results, don't prioritize featured article layout
  if (searchActive) {
    return (
      <div className={styles.searchResultsGrid}>
        {articles.map((article, index) => (
          <EnhancedArticleCard 
            key={article.id} 
            article={article} 
            priority={index < 3}
            showCategory={true}
          />
        ))}
      </div>
    );
  }
  
  // Default layout with featured article
  const featuredArticle = articles[0];
  const remainingArticles = articles.slice(1);
  
  return (
    <div className={styles.articlesGrid}>
      <EnhancedArticleCard article={featuredArticle} isFeatured={true} priority={true} />
      
      {remainingArticles.map((article, index) => (
        <EnhancedArticleCard 
          key={article.id} 
          article={article} 
          priority={index < 3}
        />
      ))}
    </div>
  );
}

export default function EnhancedArticlesPage({ initialArticles }: EnhancedArticlesPageProps) {
  const [articles, setArticles] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Initialize articles from props
  useEffect(() => {
    const searchResults: SearchResult[] = initialArticles.map((article: ContentfulArticle) => ({
      id: article.id || '',
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      publishDate: article.publishDate,
      tags: article.tags || [],
      category: extractCategoryFromTags(article.tags),
      featuredImage: article.featuredImage,
      author: article.author
    }));
    
    setArticles(searchResults);
    setTotalCount(searchResults.length);
  }, [initialArticles]);

  const handleSearchResults = (results: SearchResult[]) => {
    setArticles(results);
    setTotalCount(results.length);
    setSearchActive(true);
  };

  const handleSearchLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  return (
    <div className={styles.articlesContainer}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Articles & Insights</h1>
        <p className={styles.pageDescription}>
          Explore my thoughts on digital marketing, analytics, and technology to help you navigate the digital landscape.
        </p>
      </div>
      
      {/* Enhanced search and filtering */}
      <ArticleSearch 
        onResults={handleSearchResults}
        onLoading={handleSearchLoading}
        className={styles.searchSection}
      />
      
      {/* Results header */}
      {!isLoading && (
        <div className={searchStyles.resultsHeader}>
          <div className={searchStyles.resultsCount}>
            {searchActive ? `${totalCount} result${totalCount !== 1 ? 's' : ''} found` : `${totalCount} article${totalCount !== 1 ? 's' : ''}`}
          </div>
        </div>
      )}
      
      {/* Articles grid */}
      <ArticlesGrid 
        articles={articles} 
        isLoading={isLoading} 
        searchActive={searchActive}
      />
    </div>
  );
}
